/**
 * ValidadeLegend - Legenda de cores para validade de produtos
 * Epic 49: Sistema de cores por níveis de vencimento
 *
 * 4 níveis baseados nos thresholds do cliente:
 * - Crítico: dias < sinalizar_vencimento_1 (vermelho)
 * - Nível 1: sinalizar_vencimento_1 <= dias < sinalizar_vencimento_2 (laranja)
 * - Nível 2: sinalizar_vencimento_2 <= dias < sinalizar_vencimento_3 (amarelo)
 * - OK: dias >= sinalizar_vencimento_3 (verde)
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ValidadeLegendProps {
  className?: string;
}

export const ValidadeLegend: React.FC<ValidadeLegendProps> = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-4 text-xs px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 ${className}`}>
      <span className="font-medium text-gray-700 dark:text-gray-300">Vencimento:</span>

      {/* Crítico - Vermelho */}
      <span className="flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
        <AlertTriangle className="w-3 h-3 text-red-500" />
        <span className="text-gray-600 dark:text-gray-400">Crítico</span>
      </span>

      {/* Nível 1 - Laranja */}
      <span className="flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
        <span className="text-gray-600 dark:text-gray-400">Alerta 1</span>
      </span>

      {/* Nível 2 - Amarelo */}
      <span className="flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
        <span className="text-gray-600 dark:text-gray-400">Alerta 2</span>
      </span>

      {/* OK - Verde */}
      <span className="flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
        <span className="text-gray-600 dark:text-gray-400">OK</span>
      </span>
    </div>
  );
};

/**
 * Helper function to get validade style based on days until expiration
 * Used by ItensContagemTab, ConferenciaLacresTab, and CirurgiasTab
 */
export function getValidadeStyle(
  diasParaVencer: number | undefined | null,
  nivel1: number = 90,
  nivel2: number = 120,
  nivel3: number = 180
): { textColor: string; showIcon: boolean } {
  if (diasParaVencer === null || diasParaVencer === undefined) {
    return { textColor: 'text-gray-400', showIcon: false };
  }

  if (diasParaVencer < nivel1) {
    // CRÍTICO - Vermelho + Ícone
    return { textColor: 'text-red-600 dark:text-red-400 font-semibold', showIcon: true };
  } else if (diasParaVencer < nivel2) {
    // ALERTA 1 - Laranja
    return { textColor: 'text-orange-600 dark:text-orange-400', showIcon: false };
  } else if (diasParaVencer < nivel3) {
    // ALERTA 2 - Amarelo
    return { textColor: 'text-yellow-600 dark:text-yellow-400', showIcon: false };
  } else {
    // SEGURO - Verde
    return { textColor: 'text-green-600 dark:text-green-400', showIcon: false };
  }
}

export default ValidadeLegend;
